import { useMemo } from 'react';

import { DeserializedUserPublicProfile } from '@api';

import KarmaPoint from '@uikit/icons/KarmaPoint';

import NextLink from '@shared/common/components/NextLink';
import ReadMoreText from '@shared/common/components/ReadMoreText';
import { useAuth } from '@shared/common/providers/AuthProvider';

import Avatar from '@store:web/components/common/Avatar/Avatar';

import AppBlock from './AppBlock';

const UserInfo = ({ data }: { data: DeserializedUserPublicProfile }) => {
  const { user, isLoading } = useAuth();
  const isSameUser = useMemo(() => {
    return !isLoading && user?.attributes.name === data.attributes.magicId;
  }, [user, isLoading, data]);

  return (
    <div className="w-full md:max-w-[380px] text-center lg:text-left flex flex-col items-center lg:items-start">
      <Avatar
        isLink={false}
        userInfo={{
          magicId: data.attributes.magicId,
          displayName: data.attributes.displayName,
          membership: {
            tier: data?.membershipTier?.attributes?.key,
          },
        }}
        src={data.attributes.avatar}
        sizeInPX={200}
        quality={90}
        containerClassName="w-[125px] h-[125px] lg:w-[200px] lg:h-[200px] rounded-full overflow-hidden mt-[-25px] lg:mt-[-200px] mb-2"
      />

      <h1 className="t-h2 text-neutral-600 pb-1 truncate w-full" data-qa="magicId-account">
        {data.attributes.magicId}
      </h1>
      <h2 className="t-h3 text-neutral-200 pb-4 truncate w-full" data-qa="display-name">
        {data.attributes.displayName}
      </h2>
      <p className="text-sm font-light text-neutral-600 mb-4">
        <ReadMoreText text={data.attributes.description || ''} />
      </p>
      <div className="w-full bg-secondary rounded-sm flex flex-col justify-center items-center h-[60px] text-neutral-50 mb-4">
        <span>Karma Points</span>
        <span data-qa="karma-points">
          <KarmaPoint className="icon" />
          {data.attributes.karmaPoints}
        </span>
      </div>
      {isSameUser && (
        <NextLink
          className="button button-primary button-lg w-full font-semibold rounded-full mb-8"
          href={'/profile/settings'}
        >
          Edit
        </NextLink>
      )}
      <AppBlock
        apps={data.appsFollowed || []}
        title="Follows"
        link={`/user/${data.attributes.magicId}/follows`}
        isMiniMobile={true}
        size="sm"
      />
    </div>
  );
};

export default UserInfo;
